import React, { useEffect } from "react"
import { View } from "react-native"
import ReactMarkdown from "react-markdown"
import remarkMath from "remark-math"
import rehypeKatex from "rehype-katex"
import rehypeRaw from "rehype-raw"
import rehypeSanitize from "rehype-sanitize"
import { theme } from "@common/theme/theme"
import { SANITIZE_SCHEMA } from "../../../tutor/home/MarkdownWithLatex.web"
import { ensureRichTextVendorCss } from "../../../design-system/molecules/RichText/richTextVendorCss"
import { remarkEnonceLatex } from "@common/utils/enonceLatex"
import { splitLatexSegments } from "./latexSegments"
import { rescueLooseLatex } from "./latexToUnicode"

/**
 * Rendu d'un message de dialogue : Markdown allégé + mathématiques.
 *
 * Version WEB. Elle emprunte la MÊME chaîne que l'affichage des énoncés
 * (`MarkdownWithLatex.web`) : `remarkMath` + `remarkEnonceLatex`, puis
 * `rehypeRaw` → `rehypeSanitize` → `rehypeKatex`. Une bulle et un énoncé
 * doivent lire le même LaTeX de la même façon — deux chaînes divergentes, ce
 * sont deux jeux de défauts à corriger deux fois, et une politique de
 * nettoyage qui s'ouvre d'un côté seulement.
 *
 * `remarkEnonceLatex` travaille sur l'ARBRE, après le parseur : il sait ce qui
 * est formule, texte ou code. C'est ce qui manque à une réparation faite sur
 * le texte brut, qui doit deviner — et se trompait, par exemple, sur une
 * apostrophe à l'intérieur d'un `\text{…}` (corrigé dans `looksLikeProse`,
 * `latexSegments.js`).
 *
 * Un défaut n'a PAS d'équivalent côté énoncé, et reste géré ici : Sam oublie
 * parfois les `$` (« A = \pi \times r^2 » tout court plutôt que « $A = …$ »).
 * Un énoncé — contenu généré et relu — n'a pas ce problème ; une réponse de
 * chat, si. `splitLatexSegments` isole donc d'abord la prose des formules
 * (avec la même normalisation de délimiteurs que l'énoncé), puis
 * `rescueLooseLatex` rattrape les antislashs oubliés dans la prose SEULE —
 * jamais à l'intérieur d'une formule déjà délimitée, où il abîmerait un vrai
 * LaTeX. Le texte reconstruit retraverse ensuite exactement la chaîne d'un
 * énoncé.
 *
 * Seul le NATIF diverge, et pour une raison mesurée : là-bas l'énoncé est rendu
 * par une WebView (Mathpix ~4,5 Mo + KaTeX ~1,4 Mo). `StructuredCorrectionBlock`
 * documente déjà la parade — n'en monter qu'UNE à la fois — ce qui ne se
 * transpose pas à une conversation qui affiche N bulles et défile. Voir
 * `RichText.js`.
 */

// La feuille de KaTeX et ses polices (~0,4 Mo) passent par le seul point de
// chargement des moteurs web, `ensureRichTextVendorCss` : idempotent, le premier
// composant monté les pose, les suivants ne font rien.

/**
 * Reconstruit une chaîne Markdown+LaTeX à partir des segments partagés.
 *
 * `splitLatexSegments` a déjà nettoyé les caractères de contrôle et normalisé
 * les délimiteurs (même passage que l'énoncé) ; il ne reste qu'à re-poser des
 * `$…$` autour de chaque formule pour `remark-math`, et à rattraper la prose
 * qui en est dépourvue.
 */
const prepare = (content) =>
    splitLatexSegments(String(content))
        .map((segment) => {
            if (segment.type !== "math") return rescueLooseLatex(segment.value)
            const fence = segment.display ? "$$" : "$"
            return `${fence}${segment.value}${fence}`
        })
        .join("")

// Les plugins et les composants de la bulle, créés une fois : recréés à chaque
// rendu, ils relançaient tout le pipeline Markdown + KaTeX de chaque bulle.
const REMARK_PLUGINS = [remarkMath, remarkEnonceLatex]
const REHYPE_PLUGINS = [rehypeRaw, [rehypeSanitize, SANITIZE_SCHEMA], rehypeKatex]
const BUBBLE_COMPONENTS = {
    // Une bulle n'est pas un document : pas de marge de paragraphe, pas de
    // titres qui hurlent.
    p: ({ children }) => <p style={{ margin: 0 }}>{children}</p>,
    ul: ({ children }) => (
        <ul style={{ margin: "4px 0", paddingLeft: 18 }}>{children}</ul>
    ),
    ol: ({ children }) => (
        <ol style={{ margin: "4px 0", paddingLeft: 18 }}>{children}</ol>
    ),
    h1: ({ children }) => <strong>{children}</strong>,
    h2: ({ children }) => <strong>{children}</strong>,
    h3: ({ children }) => <strong>{children}</strong>,
}

const RichText = ({ content, ...textProps }) => {
    useEffect(() => {
        void ensureRichTextVendorCss()
    }, [])

    if (!content) return null

    const fontSize = textProps.fontSize || 14
    const color = textProps.color || theme.colors.dark
    const source = prepare(content)

    return (
        <View style={{ flexShrink: 1, minWidth: 0 }}>
            <div
                className="masteur-richtext"
                style={{
                    fontFamily: "Montserrat-Regular",
                    fontSize,
                    lineHeight: 1.45,
                    color,
                    // La bulle borne déjà la largeur ; une formule trop longue
                    // défile dans son propre cadre plutôt que d'élargir la
                    // conversation.
                    overflowX: "auto",
                    overflowY: "hidden",
                }}
            >
                <ReactMarkdown
                    remarkPlugins={REMARK_PLUGINS}
                    rehypePlugins={REHYPE_PLUGINS}
                    components={BUBBLE_COMPONENTS}
                >
                    {source}
                </ReactMarkdown>
            </div>
        </View>
    )
}

// Une bulle déjà affichée ne change pas : elle ne se re-rend pas quand le fil
// bouge autour d'elle.
export default React.memo(RichText)
